import R from 'ramda';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';

import {
  ALL_DATA_TABLE_CATEGORY_COLUMNS,
  ALL_DATA_TABLE_OFFER_COLUMNS,
  ALL_DATA_TABLE_OPTION_COLUMNS,
  PAGE_SIZE,
  SPREADSHEET_TO_VALIDATION_ARRAY_OFFSET,
  sortByCriteria,
  statusFilter,
  tableView,
} from './constants';
import { WithRowNumber } from '../../libs/googleSheets';
import { ShapedLine } from '../../libs/googleSheets/models';
import { CategoryLine, OfferLine, OptionLine, ValidationError } from '../../libs/googleSheets/types';
import { buildErrorsMap, getOptionsWithoutEmptyLines, isOptionLineType } from '../../utils';

type UseDataTableProps = {
  offers: {
    data?: WithRowNumber<Partial<OfferLine>>[];
    errors?: ValidationError[];
  };
  options: {
    data?: WithRowNumber<Partial<OptionLine>>[];
    errors?: ValidationError[];
  };
  categories: {
    data?: WithRowNumber<Partial<CategoryLine>>[];
    errors?: ValidationError[];
  };
};

export type DataTableView = 'offers' | 'options' | 'categories';
export type SortByCriteria = 'ERRORS' | 'ADDITIONS' | 'UPDATES' | 'NONE';
export type StatusFilter = 'ALL' | 'ERRORS' | 'ADDITIONS' | 'UPDATES';
export type DataTableColumn<T> = {
  value: keyof WithRowNumber<T> | 'status';
  label: string;
  defaultChecked?: boolean;
  disabled?: boolean;
};

export type ColumnsToDisplay = {
  offers: DataTableColumn<OfferLine>[];
  options: DataTableColumn<OptionLine>[];
  categories: DataTableColumn<CategoryLine>[];
};

export type DataTableState = {
  currentView: DataTableView;
  setCurrentView: (view: DataTableView) => void;
  columnsToDisplay: ColumnsToDisplay;
  setColumnsToDisplay: Dispatch<SetStateAction<ColumnsToDisplay>>;
  dataToDisplay: WithRowNumber<Partial<ShapedLine>>[];
  getRowErrorMessage: (rowLine: number) => string[] | undefined;
  appliedSortBy: SortByCriteria;
  setAppliedSortBy: (sortBy: SortByCriteria) => void;
  appliedStatusFilter: StatusFilter;
  setAppliedStatusFilter: (statusFilter: StatusFilter) => void;
  pagination: {
    goToNextPage: () => void;
    goToPreviousPage: () => void;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    listLength: number;
    currentRange: string;
  };
};

export const useDataTable = (props: UseDataTableProps): DataTableState => {
  const [currentView, setCurrentView] = useState<DataTableView>(tableView.OFFERS);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [appliedSortBy, setAppliedSortBy] = useState<SortByCriteria>('ERRORS');
  const [appliedStatusFilter, setAppliedStatusFilter] = useState<StatusFilter>(statusFilter.ALL);
  const [columnsToDisplay, setColumnsToDisplay] = useState<ColumnsToDisplay>({
    offers: ALL_DATA_TABLE_OFFER_COLUMNS.filter(({ defaultChecked }) => defaultChecked),
    options: ALL_DATA_TABLE_OPTION_COLUMNS.filter(({ defaultChecked }) => defaultChecked),
    categories: ALL_DATA_TABLE_CATEGORY_COLUMNS.filter(({ defaultChecked }) => defaultChecked),
  });

  const handleCurrentViewChange = useCallback((view: DataTableView) => {
    setCurrentView(view);
    setCurrentPage(0);
  }, []);

  const errorsMap = useMemo(
    () => ({
      offers: buildErrorsMap(props.offers.errors),
      options: buildErrorsMap(props.options.errors),
      categories: buildErrorsMap(props.categories.errors),
    }),
    [props],
  );

  const getFilteredData = useCallback(
    (data: WithRowNumber<Partial<ShapedLine>>[]) => {
      switch (appliedStatusFilter) {
        case statusFilter.ERRORS:
          return data.filter(rowObject =>
            errorsMap[currentView].has(rowObject.rowNumber - SPREADSHEET_TO_VALIDATION_ARRAY_OFFSET),
          );

        case statusFilter.UPDATES:
          return data.filter(
            rowObject =>
              (isOptionLineType(rowObject) ? !!rowObject?.option_value_id : !!rowObject?.id) &&
              !errorsMap[currentView].has(rowObject.rowNumber - SPREADSHEET_TO_VALIDATION_ARRAY_OFFSET),
          );

        case statusFilter.ADDITIONS:
          return data.filter(
            rowObject =>
              (isOptionLineType(rowObject) ? !rowObject?.option_value_id : !rowObject?.id) &&
              !errorsMap[currentView].has(rowObject.rowNumber - SPREADSHEET_TO_VALIDATION_ARRAY_OFFSET),
          );

        case statusFilter.ALL:
        default:
          return data;
      }
    },
    [appliedStatusFilter, currentView, errorsMap],
  );

  const getPaginatedData = useCallback(
    (data: WithRowNumber<Partial<ShapedLine>>[]) => data?.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE),
    [currentPage],
  );

  const getSortedData = useCallback(
    (data: WithRowNumber<Partial<ShapedLine>>[]) => {
      switch (appliedSortBy) {
        case sortByCriteria.ERRORS:
          return R.sort<WithRowNumber<Partial<ShapedLine>>>((a, b) => {
            const aHasError = errorsMap[currentView].has(a.rowNumber - SPREADSHEET_TO_VALIDATION_ARRAY_OFFSET);
            const bHasError = errorsMap[currentView].has(b.rowNumber - SPREADSHEET_TO_VALIDATION_ARRAY_OFFSET);

            return Number(bHasError) - Number(aHasError);
          }, data);

        case sortByCriteria.ADDITIONS:
          return R.sort<WithRowNumber<Partial<ShapedLine>>>((a, b) => {
            const aHasError = errorsMap[currentView].has(a.rowNumber - SPREADSHEET_TO_VALIDATION_ARRAY_OFFSET);
            const bHasError = errorsMap[currentView].has(b.rowNumber - SPREADSHEET_TO_VALIDATION_ARRAY_OFFSET);

            const aIsAddition = (isOptionLineType(a) ? !a?.option_value_id : !a?.id) && !aHasError;
            const bIsAddition = (isOptionLineType(b) ? !b?.option_value_id : !b?.id) && !bHasError;

            return Number(bIsAddition) - Number(aIsAddition);
          }, data);

        case sortByCriteria.UPDATES:
          return R.sort<WithRowNumber<Partial<ShapedLine>>>((a, b) => {
            const aHasError = errorsMap[currentView].has(a.rowNumber - SPREADSHEET_TO_VALIDATION_ARRAY_OFFSET);
            const bHasError = errorsMap[currentView].has(b.rowNumber - SPREADSHEET_TO_VALIDATION_ARRAY_OFFSET);

            const aIsUpdate = (isOptionLineType(a) ? !!a?.option_value_id : !!a?.id) && !aHasError;
            const bIsUpdate = (isOptionLineType(b) ? !!b?.option_value_id : !!b?.id) && !bHasError;

            return Number(bIsUpdate) - Number(aIsUpdate);
          }, data);

        case sortByCriteria.ALL:
        default:
          return data;
      }
    },
    [appliedSortBy, currentView, errorsMap],
  );

  const unpaginatedDataToDisplay = useMemo<WithRowNumber<Partial<ShapedLine>>[]>(() => {
    const dataToUse =
      currentView === tableView.OPTIONS
        ? getOptionsWithoutEmptyLines(props[currentView].data ?? [])
        : props[currentView].data;

    if (!dataToUse || !dataToUse.length) {
      return [];
    }

    return getSortedData(getFilteredData(dataToUse));
  }, [props, currentView, getSortedData, getFilteredData]);

  const dataToDisplay = useMemo(
    () => getPaginatedData(unpaginatedDataToDisplay),
    [getPaginatedData, unpaginatedDataToDisplay],
  );

  const getRowErrorMessage = useCallback(
    (rowNumber: number) => {
      const validatedPayloadIndex = rowNumber - SPREADSHEET_TO_VALIDATION_ARRAY_OFFSET;
      return errorsMap[currentView].get(validatedPayloadIndex);
    },
    [currentView, errorsMap],
  );

  const goToNextPage = useCallback(() => {
    setCurrentPage(currentPage + 1);
  }, [currentPage]);

  const goToPreviousPage = useCallback(() => {
    setCurrentPage(currentPage - 1);
  }, [currentPage]);

  const listLength = useMemo(() => unpaginatedDataToDisplay.length, [unpaginatedDataToDisplay.length]);

  const hasNextPage = useMemo(() => (currentPage + 1) * PAGE_SIZE < listLength, [currentPage, listLength]);

  const hasPreviousPage = useMemo(() => currentPage > 0, [currentPage]);

  const currentRange = useMemo(
    () => `${currentPage * PAGE_SIZE + (listLength ? 1 : 0)}-${Math.min((currentPage + 1) * PAGE_SIZE, listLength)}`,
    [currentPage, listLength],
  );

  return {
    setCurrentView: handleCurrentViewChange,
    columnsToDisplay,
    setColumnsToDisplay,
    dataToDisplay,
    currentView,
    getRowErrorMessage,
    appliedSortBy,
    setAppliedSortBy,
    appliedStatusFilter,
    setAppliedStatusFilter,
    pagination: {
      goToNextPage,
      goToPreviousPage,
      hasNextPage,
      hasPreviousPage,
      listLength,
      currentRange,
    },
  };
};
